import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { FiPhone } from 'react-icons/fi';
import { BsWhatsapp } from 'react-icons/bs';
import { contactConfig } from '../config/contactConfig';
import { pushDataLayer } from '../utils/analytics';
import trafficClient from '../utils/trafficClient';

const Layout = ({ children, title, description }) => {
  const location = useLocation();
  const defaultTitle = 'Olgun Kepenk - Kaliteli ve Güvenilir Kepenk Sistemleri';
  const defaultDescription =
    'İstanbul genelinde otomatik kepenk servisi, kepenk tamiri, bakım, kumanda, motor ve sıfır kepenk çözümleri.';

  useEffect(() => trafficClient.install(), []);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      trafficClient.pageView();
      pushDataLayer('virtual_page_view', {
        page_location: window.location.href,
        page_path: `${location.pathname}${location.search}`,
        page_title: document.title
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleContactClick = (event) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      const link = event.target.closest('a[href]');
      if (!link) {
        return;
      }

      const href = link.getAttribute('href') || '';
      const isPhone = href.startsWith('tel:');
      const isWhatsApp = href.includes('wa.me/') || href.includes('api.whatsapp.com/');

      if (!isPhone && !isWhatsApp) {
        return;
      }

      pushDataLayer(isPhone ? 'phone_click' : 'whatsapp_click', {
        contact_method: isPhone ? 'phone' : 'whatsapp',
        link_url: href,
        page_path: `${window.location.pathname}${window.location.search}`,
        link_text: (link.textContent || link.getAttribute('aria-label') || '').trim()
      });
      trafficClient.track(isPhone ? 'phone_click' : 'whatsapp_click');
    };

    document.addEventListener('click', handleContactClick, true);
    return () => document.removeEventListener('click', handleContactClick, true);
  }, []);

  return (
    <>
      <Helmet>
        <title>{title || defaultTitle}</title>
        <meta name="description" content={description || defaultDescription} />
        <meta property="og:title" content={title || defaultTitle} />
        <meta
          property="og:description"
          content={description || defaultDescription}
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://www.olgunkepenk.com${location.pathname}`} />
        <link rel="canonical" href={`https://www.olgunkepenk.com${location.pathname}`} />
        <meta name="robots" content="index, follow" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0"
        />
        <html lang="tr" />
      </Helmet>
      <div className="flex min-h-screen flex-col pb-[72px] md:pb-0">
        <Header />
        <main className="flex-grow pt-20 lg:pt-24">
          {children}
        </main>
        <Footer />

        <div className="fixed inset-x-0 bottom-0 z-50 grid h-[72px] grid-cols-2 gap-2 border-t border-white/15 bg-[#111817] p-2.5 shadow-[0_-8px_24px_rgba(0,0,0,0.18)] md:hidden">
          <a
            href={`tel:${contactConfig.phone}`}
            className="flex min-w-0 items-center justify-center gap-2 rounded-xl bg-[#d8ba86] px-3 text-sm font-bold text-[#111817]"
            aria-label="Bizi Arayın"
          >
            <FiPhone className="h-5 w-5 shrink-0" aria-hidden="true" />
            <span>Ara</span>
          </a>
          <a
            href={`https://wa.me/${contactConfig.whatsapp}?text=${encodeURIComponent(contactConfig.whatsappMessage)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex min-w-0 items-center justify-center gap-2 rounded-xl border border-white/35 bg-white/5 px-3 text-sm font-semibold text-white"
            aria-label="WhatsApp ile İletişime Geçin"
          >
            <BsWhatsapp className="h-5 w-5 shrink-0" aria-hidden="true" />
            <span>WhatsApp</span>
          </a>
        </div>
      </div>
    </>
  );
};

export default Layout;
